import React from "react";
import type { FloorOption } from "../types";
import Text from "./Text";
import { useTranslation } from "react-i18next";

interface Props {
  currentFloorId: string;
  floors?: FloorOption[];
  onFloorChange: (floorId: string) => void;
  isMobile?: boolean;
}

const MapFloorSelector: React.FC<Props> = ({
  currentFloorId,
  floors = [],
  onFloorChange,
  isMobile = false,
}) => {
  // 翻訳インスタンスのインポート
  const { t } = useTranslation();

  if (isMobile) {
    return null;
  }

  const selectFloor = (floorId: string) => {
    if (floorId !== currentFloorId) onFloorChange(floorId);
  };

  const extractNumber = (floor: FloorOption) => {
    const idMatch = floor.id.match(/(\d+)/);
    if (idMatch) return Number(idMatch[1]);
    return 0;
  };

  const mainFloors = floors
    .filter((f) => f.id.startsWith("main"))
    .sort((a, b) => extractNumber(a) - extractNumber(b));
  const researchFloors = floors
    .filter((f) => f.id.startsWith("research"))
    .sort((a, b) => extractNumber(a) - extractNumber(b));

  const renderFloorButton = (floor: FloorOption) => {
    const isActive = currentFloorId === floor.id;
    const num = extractNumber(floor);

    const baseClasses = [
      "flex",
      "items-center",
      "justify-center",
      "rounded-[1.25rem]",
      "border",
      "w-[4.25rem]",
      "h-[4.25rem]",
      "border-Border",
      "transition-[background-color,transform]",
      "duration-150",
      "active:scale-95",
    ];

    const colorClasses = isActive
      ? ["bg-MiraiPrimary", "text-white", "active:bg-[#9a0001]"]
      : ["bg-white", "text-TextPrimary", "hover:bg-Background", "active:bg-Border"];

    const classes = [...baseClasses, ...colorClasses].join(" ");

    return (
      <button
        key={floor.id}
        className={classes}
        onClick={() => selectFloor(floor.id)}
        aria-pressed={isActive}
        aria-label={`${floor.label}`}
      >
        <div className="flex items-baseline">
          <Text
            as="span"
            variant="body/l"
            weight="bold"
            style={isActive ? { color: "#fff" } : undefined}
          >
            {num}
          </Text>
          <Text
            as="span"
            variant="body/m"
            weight="bold"
            style={isActive ? { color: "#fff" } : undefined}
          >
            {t("階")}
          </Text>
        </div>
      </button>
    );
  };

  return (
    // backdrop-blur は地図上で重いため使わない
    <div className="hidden map-floor-selector text-center rounded-4xl py-12 bg-white/85 sm:block">
      <div className="space-y-8">
        <div>
          <Text
            as="div"
            variant="body/m"
            weight="bold"
            className="leading-tight whitespace-normal break-words text-center w-[7rem] max-w-[7rem] mx-auto"
          >
            {t("本部棟")}
          </Text>
          <div className="mt-4 flex flex-col gap-4 items-center">
            {mainFloors.map((floor) => renderFloorButton(floor))}
          </div>
        </div>

        <div>
          <Text
            as="div"
            variant="body/m"
            weight="bold"
            className="leading-tight whitespace-normal break-words text-center w-[7rem] max-w-[7rem] mx-auto"
          >
            {t("研究棟")}
          </Text>
          <div className="mt-4 flex flex-col gap-4 items-center">
            {researchFloors.map((floor) => renderFloorButton(floor))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapFloorSelector;
